// START IMPORTS
import { getCollectionNameFromCollectionId } from '@ltvco/refresh-lib/hooks';
import { useMonitorUpsell } from '@ltvco/refresh-lib/payments';
import { Stack, Text } from '@ltvco/refresh-lib/theme';
import {
  ListAction,
  MenuItem,
  MonitorAction,
  MoreAction,
  PdfAction,
  RecentReportSerialized,
  SaveAction,
  RECENT_REPORTS_DATA_MATCHER,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';

import { useMonitorUpsellModalContext } from 'context/MonitorUpsellModalContext';

import { generateReportSubtitle } from '../utils';

import { DescriptionDates } from './DescriptionDates';
import { PropertySizeDisplay } from './PropertySizeDisplay';
// END IMPORTS

// START INTERFACES_AND_TYPES
type RecentReportsCardDescriptionProps = {
  report: RecentReportSerialized;
  // eslint-disable-next-line no-unused-vars
  deleteCurrentReport: (permalink: string) => void;
  hideRats?: boolean;
  isOnDashboard?: boolean;
};
// END INTERFACES_AND_TYPES

// START CONSTANTS
const hidePDFReportTypes = [
  'username',
  'obituary',
  'unclaimed money',
  'ancestor',
];

const hideNotesReportTypes = ['unclaimed money'];

const fraudReportTypes = [
  'phone fraud',
  'email fraud',
  'ip fraud',
  'url fraud',
];
// END CONSTANTS

export const RecentReportsCardDescription: React.FC<
  RecentReportsCardDescriptionProps
> = ({
  report,
  deleteCurrentReport,
  hideRats = false,
  isOnDashboard = false,
}) => {
  // START HOOKS_AND_QUERIES
  const isInDataVariation = useFeatureIsOn('OAR-1540');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const { shouldShowMonitoringAddonUpsell } = useMonitorUpsell();
  const monitorUpsellModal = useMonitorUpsellModalContext();
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const { reportTypeFormatted, id: reportPermaLink, reportData } = report;
  const reportType = reportTypeFormatted.toLowerCase();
  const rawReportType = report.reportType;
  const hidePDF =
    fraudReportTypes.includes(reportType.toLocaleLowerCase()) ||
    hidePDFReportTypes.includes(reportType.toLocaleLowerCase());
  const hideNotes = hideNotesReportTypes.includes(
    reportType.toLocaleLowerCase()
  );

  const isCollectionRecord = rawReportType.startsWith('fam_');

  const shouldShowPropertySizeData =
    reportType === 'property' && isInDataVariation;

  const subtitle = generateReportSubtitle(
    report,
    reportType,
    isInDataVariation
  );

  const filterMenuItems = (items: MenuItem[]) => {
    return items.filter(
      (item) =>
        (item.display === 'Notes' && !hideNotes && !isCollectionRecord) ||
        item.display === 'Delete'
    );
  };

  const hideMonitorForMultiState =
    reportType === 'unclaimed money' && reportPermaLink.length > 54;

  const buildReportDataForListV2 = () => {
    const matcher = RECENT_REPORTS_DATA_MATCHER[reportType];
    if (matcher) {
      return matcher(report);
    }
  };

  const reportDescription = isCollectionRecord
    ? getCollectionNameFromCollectionId(report.details?.collection_id || '')
    : `${report.reportTypeFormatted} Report`;
  // END DERIVED_DATA_AND_UTILS

  // START RENDER
  return (
    <Stack spacing={1}>
      <Stack spacing={0.5}>
        {subtitle && <Text>{subtitle}</Text>}
        {shouldShowPropertySizeData && (
          <PropertySizeDisplay details={report.details} />
        )}
        <Text>{reportDescription}</Text>
        <DescriptionDates
          updatedDate={report.updatedDate}
          createdDate={report.createdDate}
          isOnDashboard={isOnDashboard}
          lastViewedDate={report.lastViewedDate}
        />
      </Stack>
      <Stack
        direction={isCollectionRecord ? 'row-reverse' : 'row'}
        spacing={1}
        ml={'-7px !important'}
      >
        {!hideRats && (
          <>
            {!isCollectionRecord && (
              <SaveAction
                reportPermaLink={reportPermaLink}
                onReportCard
                disableCollapse
                reportType={reportType}
                section="recent_reports_rats"
                eventName="feature_engagement"
                reportDataForListV2={
                  isInListV2Variation ? buildReportDataForListV2() : null
                }
              />
            )}
            {reportType !== 'username' &&
              !hideMonitorForMultiState &&
              !isCollectionRecord && (
                <MonitorAction
                  reportPermaLink={reportPermaLink}
                  reportType={reportType}
                  onReportCard
                  disableCollapse
                  section="recent_reports_rats"
                  eventName="feature_engagement"
                  shouldShowMonitoringAddonUpsell={
                    shouldShowMonitoringAddonUpsell
                  }
                  onReportMonitorUpsellClick={
                    monitorUpsellModal?.openMonitoringUpsellConfirmModal
                  }
                />
              )}
            {!isCollectionRecord && (
              <ListAction
                reportTitle={
                  reportTypeFormatted.toLowerCase() === 'vehicle' &&
                  report.detailsCityState
                    ? report.detailsCityState
                    : reportData
                }
                reportPermalink={reportPermaLink}
                onReportCard
                disableCollapse
                reportType={reportType}
                section="recent_reports_rats"
                eventName="feature_engagement"
                reportDataForListV2={
                  isInListV2Variation ? buildReportDataForListV2() : null
                }
              />
            )}
            {!hidePDF && !isCollectionRecord && (
              <PdfAction
                reportPermalink={reportPermaLink}
                onReportCard
                disableCollapse
                reportType={reportType}
                section="recent_reports_rats"
                eventName="feature_engagement"
              />
            )}
            <MoreAction
              filterMenuItems={filterMenuItems}
              deleteCurrentReport={deleteCurrentReport as () => void}
              reportPermaLink={reportPermaLink}
              reportType={reportType}
              onReportCard
              disableCollapse
              section="recent_reports_rats"
              eventName="feature_engagement"
              reportDataForListV2={
                isInListV2Variation ? buildReportDataForListV2() : null
              }
            />
          </>
        )}
      </Stack>
    </Stack>
  );
  // END RENDER
};
